import type { MarketingCarouselSlide } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { MarketingCarouselSlideProps } from '../components/MarketingCarousel/MarketingCarousel.vue'

const ABSOLUTE_URL_PATTERN = /^https?:\/\//
const OWN_HOST_PATTERN = /(^|\.)lotto\.com$/
const SPANISH_LOCALE = 'es'
const TRACKING_NAME_BASE = 'https://lotto.com'

export type MarketingCarouselSlideInput = Pick<
  MarketingCarouselSlide,
  'imageAltTextEn' | 'imageAltTextEs' | 'imageUrlEn' | 'imageUrlEs' | 'linkUrl' | 'position'
>

export type MarketingCarouselOrigins = {
  backend?: string
  site?: string
}

const isOwnHost = (host: string, siteHost?: string) =>
  host === siteHost || OWN_HOST_PATTERN.test(host)

const toImageUrl = (imageUrl: string, backendOrigin?: string) => {
  if (ABSOLUTE_URL_PATTERN.test(imageUrl) || !backendOrigin) return imageUrl
  return `${backendOrigin}${imageUrl}`
}

const toLinkTarget = (linkUrl: string, siteHost?: string) => {
  if (!ABSOLUTE_URL_PATTERN.test(linkUrl)) return linkUrl

  try {
    const { hash, host, pathname, search } = new URL(linkUrl)
    if (!isOwnHost(host, siteHost)) return linkUrl

    return `${pathname}${search}${hash}`
  } catch {
    return linkUrl
  }
}

const toTrackingName = (linkUrl: string) => {
  try {
    const { origin, pathname } = new URL(linkUrl, TRACKING_NAME_BASE)
    return ABSOLUTE_URL_PATTERN.test(linkUrl) ? `${origin}${pathname}` : pathname
  } catch {
    return linkUrl
  }
}

export const mapMarketingCarouselSlides = (
  slides: MarketingCarouselSlideInput[],
  locale: string,
  origins: MarketingCarouselOrigins = {},
): MarketingCarouselSlideProps[] => {
  const isSpanish = locale === SPANISH_LOCALE

  return [...slides]
    .sort((a, b) => a.position - b.position)
    .map((slide) => ({
      altText: (isSpanish ? slide.imageAltTextEs : null) ?? slide.imageAltTextEn,
      imageUrl: toImageUrl(
        (isSpanish ? slide.imageUrlEs : null) ?? slide.imageUrlEn,
        origins.backend,
      ),
      to: toLinkTarget(slide.linkUrl, origins.site),
      trackingName: toTrackingName(slide.linkUrl),
    }))
}
